import { Briefcase, Clock, Loader2, Share2, Shield, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../../i18n/config';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { deleteCaseThunk, setNewCurrentCaseThunk } from '../../../redux/thunks/CaseThunks';
import { caseUserSelectOneObjectByQuery, userSelectOneObjectByQuery } from '../../../redux/thunks/DBServiceThunks';
import { getPermissionLabel } from '../../../utils/permissions';
import ShareCaseModal from '../../case-sharing/ShareCaseModal';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';

interface CaseItemProps {
  caseId: string;
}

/**
 * Component for displaying an individual case item
 */
const CaseItem: React.FC<CaseItemProps> = ({ caseId }) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<{ id: string, title: string } | null>(null);
  const [isSelectingCase, setIsSelectingCase] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
  const caseItem = useAppSelector(state => state.DBCaseReducer.entities[caseId]);
  const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));

  // Get only the current user's case-user entry for this case
  const caseUser = useAppSelector(state => caseUserSelectOneObjectByQuery(state.DBCaseUserReducer, {
    caseId,
    userId: currentUser?.stytchMemberId
  }));

  const organizationMembers = useAppSelector(state => state.organization.members);

  const isCurrentCase = currentCaseId === caseId;
  const userPermission = caseUser?.role

  // Check if current user is organization admin/owner
  const currentUserOrgMember = organizationMembers.find(m => m._id === currentUser?._id);
  const isOrgAdmin = currentUserOrgMember?.organizationRole === 'admin' || currentUserOrgMember?.organizationRole === 'owner';


  const handleDeleteConfirm = () => {
    if (caseToDelete) {
      dispatch(deleteCaseThunk(caseToDelete.id));
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCaseToDelete(null);
  };

  const handleDeleteRequest = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (caseItem) {
      setCaseToDelete({
        id: caseItem._id,
        title: caseItem.title
      });
      setDeleteDialogOpen(true);
    }
  };

  const handleCaseSelect = async () => {
    if (!isSelectingCase) {
      setIsSelectingCase(true);
      try {
        await dispatch(setNewCurrentCaseThunk(caseItem._id));
      } finally {
        setIsSelectingCase(false);
      }
    }
  };

  const handleShareClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setShareModalOpen(true);
  };

  if (!caseItem) return null;

  return (
    <>
      <Card
        onClick={isSelectingCase ? undefined : handleCaseSelect}
        className={`
          p-4 cursor-pointer transition-smooth relative
          ${isCurrentCase
            ? 'border-gold/50 bg-gold/8 shadow-md border-l-[3px] border-l-gold'
            : 'border-border hover:border-gold/40 hover:bg-accent/50 hover:shadow-sm'
          }
          ${isSelectingCase && 'opacity-70 cursor-default'}
        `}
        dir={getLanguageDirection(i18n.language)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex items-center justify-center rounded-lg p-2 shrink-0 ${isCurrentCase ? 'bg-gold text-primary-foreground' : 'bg-gold/10 text-gold'
              }`}>
              <Briefcase className="w-4 h-4" />
            </div>
            <h2 className="text-sm font-semibold truncate text-foreground">
              {caseItem.title}
            </h2>
            {(userPermission) && (
              <Badge
                variant={userPermission === 'editor' ? 'secondary' : 'outline'}
                className="ml-2 text-xs h-5 px-2"
              >
                <Shield className="w-3 h-3 mr-1" />
                {getPermissionLabel(userPermission)}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isOrgAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShareClick}
                disabled={isSelectingCase}
                className="h-7 w-7 shrink-0 hover:bg-gold/10 hover:text-gold"
                title={t('common.tooltips.share', 'Share')}
              >
                <Share2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDeleteRequest}
              disabled={isSelectingCase}
              className="h-7 w-7 shrink-0 hover:bg-destructive/10 hover:text-destructive"
              title={t('common.tooltips.deleteCase')}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1 mt-3 ml-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>
            {t('common.case.updated')} {new Date(caseItem.updatedAt).toLocaleDateString()}
          </span>
        </div>

        {/* Loading Overlay */}
        {isSelectingCase && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-gold" />
              <span className="text-xs font-medium text-muted-foreground">
                {t('common.auth.loading')}
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Share Modal */}
      {isOrgAdmin && caseItem && (
        <ShareCaseModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          caseId={caseItem._id}
        />
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('common.tooltips.deleteCase')}
        message={t('common.cases.confirmDelete', { title: caseToDelete?.title })}
        itemName={caseToDelete?.title}
        confirmButtonText={t('common.buttons.delete')}
        isDestructive={true}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default CaseItem;
